import type React from "react"
import { useEffect, useRef } from "react"
import { useNaverMap } from "@/hooks/useNaverMap"
import {
  selectGPS,
  selectIsGPSFetchedByUser,
  selectHasGPSData,
  setGPSFetchedByUser,
} from "@/features/maps/mapsSlice"
import { useAppDispatch, useAppSelector } from "@/app/hooks"


type MapViewProps = {
  initialCenter?: { lat: number; lng: number }
  initialZoom?: number
  onMapLoad?: (map: any) => void
  onCenterChanged?: (lat: number, lng: number) => void
  onZoomChanged?: (zoom: number) => void
}

declare global {
  interface Window {
    naver: any
  }
}

const NaverMapView: React.FC<MapViewProps> = ({
  initialCenter,
  initialZoom,
  onMapLoad,
  onCenterChanged,
  onZoomChanged,
}) => {
  const { isLoaded } = useNaverMap()
  const dispatch = useAppDispatch()
  const gpsCoordinate = useAppSelector(selectGPS)
  const isGPSFetchedByUser = useAppSelector(selectIsGPSFetchedByUser)
  const hasGPSData = useAppSelector(selectHasGPSData)
  const mapElementRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)
  const currentLocationMarkerRef = useRef<any>(null)

  // 지도 초기화
  useEffect(() => {
    if (isLoaded && mapElementRef.current && !mapInstanceRef.current) {
      const centerLat = initialCenter?.lat || gpsCoordinate.lat
      const centerLng = initialCenter?.lng || gpsCoordinate.lng
      const zoom = initialZoom || 14

      const options = {
        center: new window.naver.maps.LatLng(centerLat, centerLng),
        zoom: zoom,
        zoomControl: false,
        mapDataControl: false,
      }

      const map = new window.naver.maps.Map(mapElementRef.current, options)
      mapInstanceRef.current = map

      // 지도 중심 변경 이벤트 리스너 추가
      if (onCenterChanged) {
        window.naver.maps.Event.addListener(map, "idle", () => {
          const center = map.getCenter()
          onCenterChanged(center.lat(), center.lng())
        })
      }

      // 줌 레벨 변경 이벤트 리스너 추가
      if (onZoomChanged) {
        window.naver.maps.Event.addListener(map, "zoom_changed", () => {
          const currentZoom = map.getZoom()
          onZoomChanged(currentZoom)
        })
      }

      onMapLoad?.(map)
      console.log(
        "Naver Map initialized and passed to parent with coordinates:",
        { lat: centerLat, lng: centerLng, zoom },
      )
    }
  }, [isLoaded, initialCenter, initialZoom, onMapLoad, onCenterChanged, onZoomChanged])

  // GPS 좌표 변경 시 지도 중심 이동 및 현재 위치 마커 표시
  useEffect(() => {
    if (mapInstanceRef.current && isLoaded) {
      const newCenter = new window.naver.maps.LatLng(
        gpsCoordinate.lat,
        gpsCoordinate.lng,
      )

      // 사용자가 명시적으로 조회한 경우에만 지도 중심 이동
      if (isGPSFetchedByUser) {
        mapInstanceRef.current.setCenter(newCenter)
        console.log("Map center moved to:", gpsCoordinate)
        
        // 지도 이동 후 플래그를 다시 false로 되돌림 (한 번만 이동하도록)
        dispatch(setGPSFetchedByUser(false))
      }

      // 기존 마커가 있으면 제거
      if (currentLocationMarkerRef.current) {
        currentLocationMarkerRef.current.setMap(null)
        currentLocationMarkerRef.current = null
      }

      // GPS 데이터가 있으면 현재 위치 마커 생성 (자동 조회 또는 사용자 조회 모두)
      if (hasGPSData) {
        const marker = new window.naver.maps.Marker({
          position: newCenter,
          map: mapInstanceRef.current,
          icon: {
            content: `
            <div style="position: relative; width: 30px; height: 30px;">
              <div style="
                width: 20px;
                height: 20px;
                background-color: #4285F4;
                border: 3px solid white;
                border-radius: 50%;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              "></div>
              <div style="
                width: 30px;
                height: 30px;
                background-color: rgba(66, 133, 244, 0.2);
                border-radius: 50%;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                animation: pulse 2s infinite;
              "></div>
            </div>
            <style>
              @keyframes pulse {
                0% {
                  transform: translate(-50%, -50%) scale(1);
                  opacity: 1;
                }
                100% {
                  transform: translate(-50%, -50%) scale(1.5);
                  opacity: 0;
                }
              }
            </style>
          `,
            anchor: new window.naver.maps.Point(15, 15),
          },
          zIndex: 1000,
        })

        currentLocationMarkerRef.current = marker
      }
    }
  }, [gpsCoordinate, isLoaded, isGPSFetchedByUser, hasGPSData, dispatch])


  return (
    <div
      ref={mapElementRef}
      className="full-height"
      style={{
        width: "100%",
        position: "relative",
      }}
    >
      {!isLoaded && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
          }}
        >
          <p>네이버 지도 로딩 중...</p>
        </div>
      )}
    </div>
  )
}

export default NaverMapView
